// Unit 05 — Cabling and Physical Media (Troubleshooting Lab)
import { note, readConfirm } from '../../ui/widgets.js';
import { star } from './topologies.js';
import { ran, pinged, action, linkClean, reachClean } from './helpers.js';
import { linkOf } from '../../engine/net/sim.js';

const layout = {
  width: 940, height: 360,
  nodes: {
    PC1: { x: 90, y: 80, type: 'pc', sub: 'Office · desk 1' },
    PC2: { x: 90, y: 280, type: 'pc', sub: 'Warehouse · desk W2' },
    PC3: { x: 330, y: 300, type: 'pc', sub: 'Warehouse · desk W3' },
    SW1: { x: 470, y: 150, type: 'switch', sub: 'Comms room' },
    R1: { x: 790, y: 150, type: 'router', sub: 'Gateway 192.168.10.1' },
  },
  zones: [
    { x: 20, y: 25, w: 250, h: 120, label: 'OFFICE' },
    { x: 20, y: 200, w: 420, h: 150, label: 'WAREHOUSE (new desks)' },
    { x: 380, y: 25, w: 540, h: 170, label: 'COMMS ROOM' },
  ],
};

const REPLACEMENTS = [
  { id: 'cat6-85', label: 'Cat6 UTP, 85 m — re-pulled via the new cable tray route', type: 'Cat6 UTP', length: 85, connector: 'RJ45' },
  { id: 'cat5e-112', label: 'Cat5e UTP, 112 m — new cable, same (long) route', type: 'Cat5e UTP', length: 112, connector: 'RJ45' },
  { id: 'om4-90', label: 'OM4 multimode fibre, LC–LC, 90 m', type: 'OM4 fiber', length: 90, connector: 'LC', fits: false },
  { id: 'cat6x-85', label: 'Cat6 crossover, 85 m — via the new tray route', type: 'Cat6 UTP (crossover)', length: 85, connector: 'RJ45' },
];

function network() {
  const n = star({ pcs: ['PC1', 'PC2', 'PC3'], ports: [1, 2, 3], withRouter: true });
  const l2 = linkOf(n, 'PC2', 'FastEthernet0');
  l2.cable.seated = false;
  l2.cable.length = 40;
  const l3 = linkOf(n, 'PC3', 'FastEthernet0');
  Object.assign(l3.cable, { type: 'Cat5e UTP', length: 112, damaged: true });
  return n;
}

export default {
  id: 'u05', num: '05', title: 'Cabling and Physical Media', type: 'Troubleshooting Lab', implemented: true,
  objective: 'Diagnose physical-layer faults from switch and endpoint evidence, inspect the cabling, and prove the fix with clean counters.',
  network, layout,
  physical: { replacements: REPLACEMENTS },
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'Physical media essentials', workspace: false,
      content: `
<table>
<tr><th>Medium</th><th>Max distance</th><th>Connectors</th><th>Typical use</th></tr>
<tr><td>Cat5e / Cat6 UTP</td><td><b>100 m</b> per channel (90 m permanent link + patch cords)</td><td>RJ45</td><td>Desks, APs, phones (PoE)</td></tr>
<tr><td>Multimode fibre (OM3/OM4)</td><td>~300–550 m at 10 Gb/s</td><td>LC, SC in SFP/SFP+</td><td>Building backbone, switch uplinks</td></tr>
<tr><td>Single-mode fibre (OS2)</td><td>10 km – 80 km+</td><td>LC, SC</td><td>Campus, WAN, carrier handoffs</td></tr>
</table>
<ul>
<li><b>Straight-through vs crossover</b>: modern ports support <b>Auto-MDIX</b>, so either works; old gear needed crossover between like devices.</li>
<li>A copper port cannot take a fibre cable: you need the right transceiver (SFP) and connector.</li>
</ul>
<h3>What physical faults look like</h3>
<table>
<tr><th>Evidence</th><th>Likely cause</th></tr>
<tr><td>PC: <i>Media disconnected</i>; switch: <code>notconnect</code>; LED off</td><td>No link: unplugged, broken cable, dead port/NIC</td></tr>
<tr><td>Link up but <b>CRC / input errors</b> rising, intermittent drops, half duplex, late collisions</td><td>Damaged cable, over-length run, interference, duplex mismatch</td></tr>
</table>
<div class="callout warn">Counters are cumulative. After a fix, <code>clear counters</code>, generate traffic, and check again — otherwise old errors look like new ones.</div>`,
      widgets: [readConfirm({ id: 'u05-read' })],
      question: 'Why can a link be “up” and still be a physical-layer problem?',
    },
    {
      id: 'investigate', phase: 'INVESTIGATE', title: 'Two new warehouse desks',
      devices: ['PC2', 'PC3', 'SW1'],
      ticket: { id: '1005', priority: 3, from: 'Warehouse supervisor', site: 'BRANCH-01', subject: 'New warehouse PCs: one dead, one flaky', body: 'Desk W2 shows “no network” all the time. Desk W3 connects, but Teams calls break up and big file copies fail halfway. The office PCs are fine.' },
      content: `<p>Collect evidence from the endpoints and the switch first. Then <b>click the cables in the diagram</b> to inspect them physically — that is the walk to the warehouse.</p>`,
      evidence: [
        { id: 'pc2', text: 'PC2: adapter state captured', match: ran('PC2', ['ipconfig', 'ipconfig /all']) },
        { id: 'status', text: 'SW1: port status table captured', match: ran('SW1', 'show interfaces status') },
        { id: 'pc3ping', text: 'PC3: gateway ping run from the affected desk', match: pinged('PC3', '192.168.10.1') },
        { id: 'crc', text: 'SW1: error counters on PC3’s port captured', match: ran('SW1', 'show interfaces <IF>', /FastEthernet0\/3[\s\S]*[1-9]\d* CRC/) },
        { id: 'insp2', text: 'Cable to desk W2 physically inspected', match: action('inspect-cable', (e) => /PC2/.test(e.link)) },
        { id: 'insp3', text: 'Cable to desk W3 physically inspected', match: action('inspect-cable', (e) => /PC3/.test(e.link)) },
      ],
      question: 'PC3’s port says <code>connected</code>. What in <code>show interfaces fa0/3</code> tells you the link is still unhealthy?',
      hints: ['<code>show interfaces status</code> is the one-line-per-port view; <code>show interfaces fa0/3</code> shows the error counters.', 'Generate traffic from PC3 first (<code>ping 192.168.10.1 -n 10</code>) so the counters have something to count.', 'The inspection reveals the cable type and length. Compare the length with the Cat5e/Cat6 limit.'],
      verifyLabel: 'Evidence collected — continue',
    },
    {
      id: 'fix', phase: 'FIX', title: 'Fix the physical layer', devices: ['PC2', 'PC3', 'SW1'],
      content: '<p>Make the physical fixes by clicking each cable in the diagram. Choose replacements from the spares cabinet carefully — not everything in the cabinet is suitable.</p>',
      tasks: [
        { id: 'w2', text: 'Desk W2 link is up', check: (rt) => linkClean(rt, 'SW1', 'FastEthernet0/2'), fail: 'SW1 Fa0/2 still has no clean link.' },
        { id: 'w3', text: 'Desk W3 link is up without errors', check: (rt) => linkClean(rt, 'SW1', 'FastEthernet0/3'), fail: 'SW1 Fa0/3 link is down or still corrupting frames.' },
        { id: 'users', text: 'PC2 and PC3 reach the gateway with no loss', check: (rt) => reachClean(rt, 'PC2', '192.168.10.1') && reachClean(rt, 'PC3', '192.168.10.1'), fail: 'A warehouse PC still cannot reach the gateway cleanly.' },
      ],
      question: 'The spares cabinet has a 112 m Cat5e and an 85 m Cat6 run. Why does length matter more than the category here?',
      hints: ['Desk W2: the connector was loose. Desk W3: the run is too long and the cable is crushed.', 'The fibre cable will not fit an RJ45 port. A crossover works today because of Auto-MDIX, but a straight-through Cat6 is the standard choice.'],
    },
    {
      id: 'verify', phase: 'VERIFY', title: 'Prove the errors have stopped', devices: ['PC2', 'PC3', 'SW1'],
      content: `<p>Prove it with <b>fresh</b> counters:</p><ol><li>SW1: <code>clear counters</code> (confirm with Enter)</li><li>PC3: generate traffic — <code>ping 192.168.10.1 -n 10</code></li><li>SW1: <code>show interfaces fa0/3</code> — the CRC counter must stay at 0</li><li>PC2: ping the gateway</li></ol>`,
      evidence: [
        { id: 'clear', text: 'SW1: counters cleared', match: (ev) => ev.device === 'SW1' && ev.key === 'clear counters' },
        { id: 'pc3', text: 'PC3: every ping to the gateway answered', match: pinged('PC3', '192.168.10.1', { success: true, all: true }) },
        { id: 'zero', text: 'SW1: Fa0/3 shows traffic with 0 CRC after clearing', match: (ev, rt) => ran('SW1', 'show interfaces <IF>', /FastEthernet0\/3 is up[\s\S]* 0 input errors, 0 CRC/)(ev) && rt.events((e) => e.device === 'SW1' && e.key === 'clear counters').length > 0 && rt.net.devices.SW1.interfaces['FastEthernet0/3'].counters.inPkts > 0 },
        { id: 'pc2', text: 'PC2: every ping to the gateway answered', match: pinged('PC2', '192.168.10.1', { success: true, all: true }) },
      ],
      widgets: [note({ id: 'u05-doc', title: 'FIELD REPORT', prompt: 'For each desk: what you observed, the physical cause, what you replaced or reseated.', min: 50 })],
      verifyLabel: 'Verify & close ticket',
    },
  ],
  review: () => '<p>Two classic physical faults: <b>no link</b> (unseated connector → <i>Media disconnected</i>, <code>notconnect</code>) and <b>bad link</b> (over-length, crushed Cat5e → link up but CRC errors and loss). Always prove the fix with cleared counters.</p>',
};
